import React, {useContext, useEffect, useMemo, useState} from "react";
import "./css/ActiveZerodhaOrdersPanel.css";
import { apiFetch as fetch } from "apiClient";
import {DataContext} from "@dashboard/contexts/DataContext";
import BuySellPopup from "@dashboard/buysellpopup/BuySellPopup"; // you already use this pattern in BuySellPopup :contentReference[oaicite:3]{index=3}

/**
 * Expected API (recommended):
 *   GET /api/kite-orders/all-orders
 *   -> returns: [{ zerodhaUserId, orderId, tradingsymbol, exchange, transactionType, product,
 *                 orderType, status, price, averagePrice, filledQuantity, quantity,
 *                 orderTimestamp, tag, remarks }, ...]
 *
 * If your backend returns a different shape, just adjust normalizeOrder().
 */
function execAmount(r) {
    const filled = num(r.filled);
    const px = num(r.avgPrice); // actual executed avg price
    if (filled <= 0 || px <= 0) return 0;
    const amt = filled * px;
    return (String(r.side).toUpperCase() === "SELL") ? -amt : amt;
}

function pendingAmount(r) {
    // Remaining qty * order price (LIMIT). MARKET pending cannot be valued reliably.
    const remaining = Math.max(0, num(r.qty) - num(r.filled));
    if (remaining <= 0) return 0;

    const isMkt = String(r.orderType).toUpperCase() === "MARKET";
    if (isMkt) return 0;

    const px = num(r.price); // keyed-in price
    if (px <= 0) return 0;
    return remaining * px;
}

function fmtMoneyINR(x) {
    const n = Number(x);
    if (!Number.isFinite(n)) return "-";
    return n.toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
}

async function cancelKiteOrder({ zerodhaUserId, orderId }) {
    const res = await fetch("/api/kite-orders/cancel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ zerodhaUserId, orderId }),
    });
    return await res.json(); // { success, message, orderId? }
}

function isTerminalOrder(tagRaw) {
    const t = (tagRaw || "").trim();
    return t.length > 0 && t.toLowerCase() !== "regular";
}

function isNonTerminalOrder(tagRaw) {
    const t = (tagRaw || "").trim();
    return t.length === 0 || t.toLowerCase() === "regular";
}

function findInstrumentForOrder(allInstrumentsList, tradingsymbol, exchange) {
    if (!Array.isArray(allInstrumentsList)) return null;
    const ts = (tradingsymbol || "").toLowerCase().trim();
    const ex = (exchange || "").toUpperCase().trim();

    // Prefer exact exchange match
    const exact = allInstrumentsList.find(i =>
        i?.tradingsymbol?.toLowerCase?.() === ts &&
        (i?.exchange || "").toUpperCase() === ex
    );
    if (exact) return exact;

    // Fallback: any exchange match for symbol
    return allInstrumentsList.find(i =>
        i?.tradingsymbol?.toLowerCase?.() === ts
    ) || null;
}


const STATUS_BUCKETS = {
    pending: new Set([
        "OPEN",
        "TRIGGER PENDING",
        "AMO REQ RECEIVED",
        "PUT ORDER REQ RECEIVED",
        "VALIDATION PENDING",
        "PARTIALLY_FILLED",     // ✅ important
        "MODIFY PENDING",
        "CANCEL PENDING",
        "UPDATE PENDING",
    ]),
    executed: new Set(["COMPLETE"]),
    rejected: new Set(["REJECTED"]),
    cancelled: new Set(["CANCELLED"]),
};


function bucketForStatus(statusRaw) {
    const s = (statusRaw || "").toUpperCase().trim();
    if (STATUS_BUCKETS.executed.has(s)) return "executed";
    if (STATUS_BUCKETS.rejected.has(s)) return "rejected";
    if (STATUS_BUCKETS.cancelled.has(s)) return "cancelled";
    // treat everything else as pending-ish
    return "pending";
}

function fmtTime(ts) {
    if (!ts) return "";
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return String(ts);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function num(x) {
    const n = Number(x);
    return Number.isFinite(n) ? n : 0;
}

function normalizeOrder(o) {
    return {
        zerodhaUserId: o.zerodhaUserId || o.userId || "",
        orderId: o.orderId || o.order_id || "",
        status: o.status || "",
        bucket: bucketForStatus(o.status),
        tradingsymbol: o.tradingsymbol || o.tradingSymbol || "",
        exchange: o.exchange || "",
        side: (o.transactionType || o.side || "").toUpperCase(),
        product: o.product || "",
        orderType: (o.orderType || "").toUpperCase(),
        price: num(o.price),
        avgPrice: num(o.averagePrice ?? o.average_price ?? o.averagePrice),
        qty: num(o.quantity),
        filled: num(o.filledQuantity ?? o.filled_quantity ?? o.filledQuantity),
        tag: o.tag || "",
        remarks: o.remarks || o.message || "",
        time: o.orderTimestamp || o.order_timestamp || o.exchangeTimestamp || o.timestamp || "",
    };
}

export default function ActiveZerodhaOrdersPanel() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    // UI controls
    const [bucket, setBucket] = useState("pending"); // pending | executed | rejected | cancelled | all
    const [query, setQuery] = useState("");
    const [activeUser, setActiveUser] = useState("ALL");
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [refreshSec, setRefreshSec] = useState(8);
    const { allInstrumentsList } = useContext(DataContext);

    const [tagMode, setTagMode] = useState("all");
// "all" | "terminalOnly" | "nonTerminalOnly"

    const [repeatOpen, setRepeatOpen] = useState(false);
    const [repeatSide, setRepeatSide] = useState("BUY");
    const [repeatInstrument, setRepeatInstrument] = useState(null);
    const [repeatPrefill, setRepeatPrefill] = useState(null);

    const load = async () => {
        setErr("");
        setLoading(true);
        try {
            const res = await fetch("/api/kite-orders/all-orders");
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            const normalized = Array.isArray(data) ? data.map(normalizeOrder) : [];
            // sort: latest first if time exists, else keep stable
            normalized.sort((a, b) => {
                const ta = new Date(a.time).getTime();
                const tb = new Date(b.time).getTime();
                if (!Number.isFinite(ta) || !Number.isFinite(tb)) return 0;
                return tb - ta;
            });
            setRows(normalized);
        } catch (e) {
            console.error(e);
            setErr("Could not load orders. Check backend endpoint /api/kite-orders/all-orders");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!autoRefresh) return;
        const ms = Math.max(3, Number(refreshSec) || 8) * 1000;
        const id = window.setInterval(load, ms);
        return () => window.clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoRefresh, refreshSec]);

    const users = useMemo(() => {
        const set = new Set(rows.map(r => r.zerodhaUserId).filter(Boolean));
        const list = Array.from(set).sort();
        return [...list, "ALL"]; // ✅ ALL at end
    }, [rows]);

    const counts = useMemo(() => {
        const c = { all: rows.length, pending: 0, executed: 0, rejected: 0, cancelled: 0 };
        for (const r of rows) c[r.bucket] = (c[r.bucket] || 0) + 1;
        return c;
    }, [rows]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();

        return rows.filter(r => {
            if (activeUser !== "ALL" && r.zerodhaUserId !== activeUser) return false;
            if (bucket !== "all" && r.bucket !== bucket) return false;

            // ✅ Tag mode filter
            if (tagMode === "terminalOnly" && !isTerminalOrder(r.tag)) return false;
            if (tagMode === "nonTerminalOnly" && !isNonTerminalOrder(r.tag)) return false;

            if (!q) return true;
            const hay = `${r.tradingsymbol} ${r.exchange} ${r.side} ${r.status} ${r.orderId} ${r.tag} ${r.remarks}`.toLowerCase();
            return hay.includes(q);
        });
    }, [rows, bucket, query, activeUser, tagMode]);

    const totals = useMemo(() => {
        // Per your rule: cancelled/rejected tabs shouldn't use this summary
        if (bucket === "cancelled" || bucket === "rejected") {
            return { traded: 0, pending: 0, pendingMarketCount: 0 };
        }

        let traded = 0;
        let pending = 0;
        let pendingMarketCount = 0;

        for (const r of filtered) {
            // Traded amount: include ANY order with filled>0 (COMPLETE, PARTIALLY_FILLED, even CANCELLED-after-partial)
            traded += execAmount(r);

            // Pending amount: only meaningful for orders with remaining qty; MARKET pending excluded
            const isLivePending =
                r.bucket === "pending" &&
                !["REJECTED", "CANCELLED", "COMPLETE"].includes(r.status?.toUpperCase());

            if (isLivePending) {
                const remaining = Math.max(0, num(r.qty) - num(r.filled));
                if (remaining > 0) {
                    if (String(r.orderType).toUpperCase() === "MARKET") {
                        pendingMarketCount++;
                    } else {
                        pending += pendingAmount(r);
                    }
                }
            }

        }

        return { traded, pending, pendingMarketCount };
    }, [filtered, bucket]);

    const handleRepeat = (r) => {
        // Allowed only for Regular or no-tag
        if (!isNonTerminalOrder(r.tag)) return;

        const inst = findInstrumentForOrder(allInstrumentsList, r.tradingsymbol, r.exchange);

        // If not found in master list, still allow minimal instrument (token might be missing)
        const instrument = inst || {
            tradingsymbol: r.tradingsymbol,
            exchange: r.exchange,
            instrumentToken: null
        };

        setRepeatSide((r.side || "").toUpperCase() === "SELL" ? "SELL" : "BUY");
        setRepeatInstrument(instrument);
        setRepeatPrefill({
            exchange: r.exchange,
            qty: r.qty || 1,
            orderType: (r.orderType || "LIMIT").toUpperCase(),
            // Repeat must ALWAYS use original order price (NOT execution price)
            price: r.price || 0,
            zerodhaUserId: r.zerodhaUserId
        });
        setRepeatOpen(true);
    };

    const handleCancel = async (r) => {
        // Only pending bucket + Regular/no-tag
        if (r.bucket !== "pending") return;
        if (!isNonTerminalOrder(r.tag)) return;

        try {
            const res = await cancelKiteOrder({ zerodhaUserId: r.zerodhaUserId, orderId: r.orderId });
            if (!res?.success) {
                alert(res?.message || "Cancel failed");
            }
        } catch (e) {
            console.error(e);
            alert("Cancel failed (network/error).");
        } finally {
            load();
        }
    };

    return (
        <div className="azop-root">
            <div className="azop-root-inner">

            <div className="azop-controls">
                <div className="azop-tabs">
                    <button
                        className={`azop-tab ${bucket === "pending" ? "active" : ""}`}
                        onClick={() => setBucket("pending")}
                    >
                        Pending {counts.pending > 0 && <span className="azop-pill">{counts.pending}</span>}
                    </button>

                    <button
                        className={`azop-tab ${bucket === "executed" ? "active" : ""}`}
                        onClick={() => setBucket("executed")}
                    >
                        Executed {counts.executed > 0 && <span className="azop-pill">{counts.executed}</span>}
                    </button>

                    <button
                        className={`azop-tab ${bucket === "rejected" ? "active" : ""}`}
                        onClick={() => setBucket("rejected")}
                    >
                        Rejected {counts.rejected > 0 && <span className="azop-pill">{counts.rejected}</span>}
                    </button>

                    <button
                        className={`azop-tab ${bucket === "cancelled" ? "active" : ""}`}
                        onClick={() => setBucket("cancelled")}
                    >
                        Cancelled {counts.cancelled > 0 && <span className="azop-pill">{counts.cancelled}</span>}
                    </button>

                    <button
                        className={`azop-tab ${bucket === "all" ? "active" : ""}`}
                        onClick={() => setBucket("all")}
                    >
                        All {counts.all > 0 && <span className="azop-pill">{counts.all}</span>}
                    </button>
                </div>
                <div className="azop-controls-right">

                <div className="azop-filters">
                    <div className="azop-tagbar">
                        <button
                            className={`azop-chip ${tagMode === "terminalOnly" ? "active" : ""}`}
                            onClick={() => setTagMode("terminalOnly")}
                            title="Show only orders with a non-Regular tag"
                        >
                            Advanced Orders
                        </button>
                        <button
                            className={`azop-chip ${tagMode === "nonTerminalOnly" ? "active" : ""}`}
                            onClick={() => setTagMode("nonTerminalOnly")}
                            title="Show orders with Regular tag or no tag"
                        >
                            Regular Orders
                        </button>
                        <button
                            className={`azop-chip ${tagMode === "all" ? "active" : ""}`}
                            onClick={() => setTagMode("all")}
                            title="Show all orders"
                        >
                            All
                        </button>
                    </div>

                    <input
                        className="azop-search"
                        placeholder="Search: symbol / orderId / tag / status…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    <div className="azop-actions">
                        <button className="azop-btn" onClick={load} disabled={loading}>
                            {loading ? "Refreshing…" : "Refresh"}
                        </button>

                        <label className="azop-toggle">
                            <input
                                type="checkbox"
                                checked={autoRefresh}
                                onChange={(e) => setAutoRefresh(e.target.checked)}
                            />
                            Auto
                        </label>

                        <div className="azop-small">
                            <span>Every</span>
                            <input
                                className="azop-sec"
                                value={refreshSec}
                                onChange={(e) => setRefreshSec(e.target.value)}
                            />
                            <span>s</span>
                        </div>
                    </div>
                </div>
                </div>
            </div>
            <div className="azop-userbar">
                {users.length === 0 ? (
                    <div className="azop-userbar-empty">No active Zerodha users.</div>
                ) : (
                    users.map((u) => {
                        let base = rows;

// if status tab is selected (not "all"), only count within that bucket
                        if (bucket !== "all") base = rows.filter(r => r.bucket === bucket);

                        if (u !== "ALL") base = base.filter(r => r.zerodhaUserId === u);

// show a single number = count in this selected section
                        const sectionCount = base.length;


                        return (
                            <button
                                key={u}
                                className={`azop-userchip ${u === activeUser ? "active" : ""}`}
                                onClick={() => setActiveUser(u)}
                                title="Click to view this user’s orders"
                            >
                                <span className="azop-userchip-id">{u}</span>
                                {/*<span className="azop-userchip-mini">
                                  {bucket === "all" ? `${sectionCount} total` : `${sectionCount} ${bucket}`}
                                </span>*/}
                            </button>
                        );
                    })
                )}
            </div>

            <div className="azop-card-head">
                <div className="azop-card-meta">{filtered.length} orders</div>
                <div className="azop-card-metrics">
                    {bucket !== "cancelled" && bucket !== "rejected" && (
                        <>
                            <div className="azop-metric">
                                <div className="k">Total traded</div>
                                <div className={`v amt ${totals.traded < 0 ? "sell" : "buy"}`}>
                                    {fmtMoneyINR(totals.traded)}
                                </div>
                            </div>

                            <div className="azop-metric">
                                <div className="k">Pending exposure (LIMIT)</div>
                                <div className="v">{fmtMoneyINR(totals.pending)}</div>
                                {totals.pendingMarketCount > 0 && (
                                    <div className="azop-hint">
                                        {totals.pendingMarketCount} MARKET pending excluded
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {err && <div className="azop-error">{err}</div>}

            <div className="azop-body">
                {filtered.length === 0 ? (
                    <div className="azop-empty">
                        <div className="azop-empty-title">No orders match your filters</div>
                        <div className="azop-empty-sub">Try switching the status tab, or clearing search.</div>
                    </div>
                ) : (
                    <div className="azop-card">
                        {/*<div className="azop-card-head">
                            <div className="azop-user">{activeUser === "ALL" ? "ALL USERS" : activeUser}</div>
                            <div className="azop-card-meta">{filtered.length} orders</div>
                        </div>*/}

                        <div className="azop-table">
                            <div className="azop-row azop-row-head">
                                <div>Time</div>
                                <div>Symbol</div>
                                <div>Side</div>
                                <div className="azop-actions-head">Status</div>
                                <div>Qty</div>
                                <div>Filled</div>
                                <div>Type</div>
                                <div>Order Px</div>
                                <div>Exec Px</div>
                                <div>Exec Amt</div>
                                <div className="azop-actions-head">Actions</div>
                                <div>Tag</div>
                            </div>

                            {filtered.map((r) =>  {
                                const ea = execAmount(r);
                                return (
                                    <div
                                    key={`${r.zerodhaUserId}-${r.orderId}-${r.time}-${r.status}`}
                                    className={`azop-row azop-row-body ${r.bucket}`}
                                    title={r.remarks || ""}
                                     >
                                    <div className="mono dim">{fmtTime(r.time)}</div>
                                    <div className="sym">
                                        <span className="sym-main">{r.tradingsymbol}</span>
                                        <span className="sym-sub">{r.exchange} • {r.product}</span>
                                    </div>
                                    <div className={`side ${r.side === "BUY" ? "buy" : r.side === "SELL" ? "sell" : ""}`}>
                                        {r.side || "-"}
                                    </div>
                                    <div className="status">
                                        <span className={`badge ${r.bucket}`}>{r.status || "-"}</span>
                                    </div>
                                    <div className="mono">{r.qty || 0}</div>
                                    <div className="mono">{r.filled || 0}</div>
                                    <div className="mono">{r.orderType || "-"}</div>
                                    <div className="mono">{r.orderType === "MARKET" ? "MKT" : (r.price || 0).toFixed(2)}</div>
                                    <div className="mono">{r.filled > 0 ? (r.avgPrice || 0).toFixed(2) : "-"}</div>
                                    <div className={`mono amt ${ea < 0 ? "sell" : "buy"}`}>
                                       {ea === 0 ? "-" : fmtMoneyINR(ea)}
                                    </div>

                                    <div className="azop-actions-cell">
                                        {isNonTerminalOrder(r.tag) && (
                                            <>
                                                {r.bucket === "pending" && (
                                                    <button className="azop-mini azop-danger" onClick={() => handleCancel(r)}>
                                                        Cancel
                                                    </button>
                                                )}

                                                <button className="azop-mini" onClick={() => handleRepeat(r)}>
                                                    Repeat
                                                </button>
                                            </>
                                        )}
                                    </div>
                                        <div className="mono dim">{r.tag || ""}</div>
                                </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
            {repeatOpen && repeatInstrument && (
                <BuySellPopup
                    open={repeatOpen}
                    side={repeatSide}
                    instrument={repeatInstrument}
                    tick={{ lastTradedPrice: repeatPrefill?.price ?? 0 }}
                    defaultZerodhaUserId={repeatPrefill?.zerodhaUserId || ""}   // ✅ ADD THIS LINE
                    onClose={() => setRepeatOpen(false)}
                    onSubmit={async (payload) => {
                        const finalPayload = {
                            ...payload,
                            zerodhaUserId:
                                repeatPrefill?.zerodhaUserId || payload.zerodhaUserId,
                            exchange:
                                repeatPrefill?.exchange || payload.exchange,
                            quantity: Number(
                                repeatPrefill?.qty || payload.quantity || 1
                            ),
                            orderType:
                                repeatPrefill?.orderType || payload.orderType,
                            price:
                                (repeatPrefill?.orderType || payload.orderType) === "MARKET"
                                    ? 0
                                    : Number(
                                        repeatPrefill?.price ||
                                        payload.price ||
                                        0
                                    ),
                            tag: "Regular",
                            variety: "REGULAR",
                        };

                        // ✅ THIS IS THE MISSING PIECE
                        const res = await fetch("/api/kite-orders/place", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify(finalPayload),
                        });

                        const data = await res.json();

                        if (data?.success) {
                            setRepeatOpen(false);
                            load();
                        }

                        // ✅ MUST return { success, message, orderId }
                        return data;
                    }}

                />
            )}
            </div>
        </div>
    );
}
